import type { ChipColor, ChipVariant } from "@legal-500/legal500-design-system";
import type {
  DiagnosticStepStatus,
  ExclusionReason,
  FirmDiagnosticsResponse,
  JurisdictionStatus,
  SubscriptionReason,
  SubscriptionWarning,
} from "@/api/marketingFirmDiagnostics.service";

/** Why the portal hides a row, in words staff can repeat to the firm. */
export const EXCLUSION_REASON_LABELS: Record<ExclusionReason, string> = {
  post_not_published: "Not published on the website",
  publication_not_active: "Publication is not active",
  researching_outside_early_release: "Still in research, outside the early-release window",
  country_not_entitled: "Firm is not entitled to this country",
  country_missing: "Row has no country, and the firm is limited to certain countries",
  firm_not_entitled: "Firm isn't entitled to the Marketing Toolkit",
  no_entitled_jurisdictions: "No subscription gives the firm a jurisdiction",
};

export function exclusionReasonLabel(reason: string): string {
  return EXCLUSION_REASON_LABELS[reason as ExclusionReason] ?? reason;
}

/** Why a subscription doesn't count toward the firm's jurisdictions. */
export const SUBSCRIPTION_REASON_LABELS: Record<SubscriptionReason, string> = {
  not_complete: "Status is not complete",
  outside_period: "Outside its start and end dates",
  feature_missing: "Package doesn't include the Marketing Toolkit",
  no_country_region: "Country / Region is blank",
  country_region_unrecognised: "Country / Region isn't recognised by the Marketing Toolkit",
};

export function subscriptionReasonLabel(reason: string): string {
  return SUBSCRIPTION_REASON_LABELS[reason as SubscriptionReason] ?? reason;
}

export const SUBSCRIPTION_WARNING_LABELS: Record<SubscriptionWarning, string> = {
  uk_single_jurisdiction: "Covers one UK jurisdiction. The UK region covers all 18.",
  worldwide_not_recognised:
    "Worldwide isn't in the Marketing Toolkit's jurisdiction list, so this subscription opens nothing.",
};

export function subscriptionWarningLabel(warning: string): string {
  return SUBSCRIPTION_WARNING_LABELS[warning as SubscriptionWarning] ?? warning;
}

export const JURISDICTION_STATUS: Record<
  JurisdictionStatus,
  { label: string; color: ChipColor; variant?: ChipVariant }
> = {
  shown: { label: "Shown", color: "success" },
  filter_skipped: { label: "Shown, no jurisdiction filter", color: "primary" },
  not_entitled: { label: "Firm not entitled", color: "error" },
  not_covered: { label: "No subscription covers it", color: "warning" },
};

export const STEP_STATUS: Record<
  DiagnosticStepStatus,
  { label: string; color: ChipColor; variant?: ChipVariant }
> = {
  pass: { label: "Pass", color: "success" },
  fail: { label: "Fail", color: "error" },
  warn: { label: "Warning", color: "warning" },
  info: { label: "Info", color: "primary" },
  // The filled default chip nearly vanishes on a dark surface, so skipped is outlined.
  skipped: { label: "Skipped", color: "default", variant: "outlined" },
  error: { label: "Error", color: "error" },
};

export const VERDICT_TITLES: Record<FirmDiagnosticsResponse["verdict"]["outcome"], string> = {
  rows: "The firm sees rows",
  no_rows: "The firm sees an empty list",
  blocked: "The firm is blocked",
};
